(() => {
  "use strict";

  const CUTOFF_PROPERTY = "_BYLCutoffAt";
  const CHECKOUT_GRACE_MS = 15 * 60 * 1000;
  const verifiedForms = new WeakSet();
  let cleanupPromise = null;

  function cutoffTime(item) {
    const raw = String(item?.properties?.[CUTOFF_PROPERTY] || "").trim();
    if (!raw) return null;

    const value = Date.parse(raw);
    return Number.isFinite(value) ? value : null;
  }

  function expiredUpdates(cart, graceMs) {
    const now = Date.now();
    const updates = {};
    const removed = [];

    for (const item of cart?.items || []) {
      const cutoff = cutoffTime(item);

      // Older cart lines without an immutable snapshot are not safe to guess at.
      if (cutoff === null || now <= cutoff + graceMs) continue;

      updates[item.key] = 0;
      removed.push(item);
    }

    return { updates, removed };
  }

  async function fetchCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    });

    if (!response.ok) throw new Error(`Cart read failed (${response.status}).`);
    return response.json();
  }

  async function removeExpired(graceMs = 0) {
    if (cleanupPromise) return cleanupPromise;

    cleanupPromise = (async () => {
      const cart = await fetchCart();
      const { updates, removed } = expiredUpdates(cart, graceMs);

      if (!removed.length) return { removed: [], cart };

      const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/update.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) throw new Error(`Expired cart cleanup failed (${response.status}).`);
      return { removed, cart: await response.json() };
    })().finally(() => {
      cleanupPromise = null;
    });

    return cleanupPromise;
  }

  function removalMessage(count) {
    return count === 1
      ? "An item was removed from your cart because its ordering deadline has passed."
      : `${count} items were removed from your cart because their ordering deadlines have passed.`;
  }

  async function cleanupOnLoad() {
    try {
      const result = await removeExpired(0);
      if (!result.removed.length) return;

      window.alert(removalMessage(result.removed.length));
      window.location.reload();
    } catch (error) {
      console.error("BYL cart deadline check:", error);
    }
  }

  document.addEventListener("submit", async event => {
    if (verifiedForms.has(event.target)) {
      verifiedForms.delete(event.target);
      return;
    }

    const submitter = event.submitter;
    if (!submitter || submitter.name !== "checkout") return;

    event.preventDefault();
    submitter.disabled = true;

    try {
      const result = await removeExpired(CHECKOUT_GRACE_MS);

      if (result.removed.length) {
        window.alert(`${removalMessage(result.removed.length)} Please review your cart before checking out.`);
        window.location.reload();
        return;
      }

      verifiedForms.add(event.target);
      submitter.disabled = false;
      event.target.requestSubmit(submitter);
    } catch (error) {
      console.error("BYL checkout deadline check:", error);
      window.alert("We could not verify the ordering deadlines. Please try checkout again.");
      submitter.disabled = false;
    }
  }, true);

  window.BYL = window.BYL || {};
  window.BYL.cartDeadlines = { removeExpired };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", cleanupOnLoad, { once: true });
  } else {
    cleanupOnLoad();
  }
})();
